/**
 * Ghép màn chơi: RunController (@ml/core) ↔ BoardScene (bảng + input) ↔ BattleView (cảnh, HUD,
 * thanh skill) ↔ Overlay (buff, thắng/thua). Luồng: thả chuỗi → stepRun → diễn bảng + battle
 * song song → hết tầng thì chọn buff → tầng kế; thắng/thua → chơi lại.
 */
import type { Application } from 'pixi.js';
import { seqOfPath, type Coord, type RunState } from '@ml/core';
import type { Config, Content } from '@ml/config';
import type { Skin } from '../art/skin.ts';
import { BattleView } from '../battle/battleView.ts';
import { boardEventsOf } from '../board/boardEvents.ts';
import { BoardScene } from '../board/boardScene.ts';
import { Overlay } from '../ui/overlay.ts';
import { RunController } from './runController.ts';

export type GameSetup = {
  readonly config: Config;
  readonly content: Content;
  readonly heroId: string;
  readonly dungeonId: string;
  readonly seed: number;
  readonly skin: Skin | null;
  readonly atlasUrl: string;
  readonly build: string;
};

export class GameScene {
  readonly run: RunController;
  readonly board: BoardScene;
  readonly battle: BattleView;
  readonly overlay: Overlay;
  /** Đang diễn một bước (bảng + battle). */
  playing = false;
  private idle: Promise<void> = Promise.resolve();

  constructor(
    app: Application,
    readonly setup: GameSetup,
  ) {
    this.run = new RunController(setup);
    this.battle = new BattleView(app.ticker, setup.skin, setup.config, setup.content, setup.build);
    app.stage.addChild(this.battle.root);
    this.overlay = new Overlay(setup.skin, setup.atlasUrl);
    const s = this.run.state;
    this.board = new BoardScene(app, setup.config, s.battle.board, this.run.linkContext(), {
      onRelease: (path) => {
        this.idle = this.release(path).then(() => undefined);
      },
      onLayout: (l) => this.battle.setLayout(l),
      onSelection: (path) => this.battle.setSelection(seqOfPath(this.board.board, path)),
    });
    this.battle.onTarget = (slot) => {
      this.battle.target = slot;
      this.battle.sync(this.run.state);
    };
    this.battle.sync(s, true);
  }

  needsRender(): boolean {
    const d = this.battle.dirty || this.battle.busy;
    this.battle.dirty = false;
    return this.board.needsRender() || d;
  }

  relayout(): void {
    this.board.relayout();
  }

  /** Chờ bước đang diễn xong. */
  settled(): Promise<void> {
    return this.idle;
  }

  async release(path: readonly Coord[]): Promise<boolean> {
    if (this.playing || this.run.state.phase !== 'battle') return false;
    const before = this.run.state;
    const r = this.run.step({ type: 'release', path, target: this.battle.target });
    if (!r.changed) {
      const inv = r.events.flatMap((e) => (e.type === 'battle' ? e.events : []));
      const reason = inv.find((e) => e.type === 'invalid');
      this.board.rejectRelease(path, reason?.type === 'invalid' ? reason.reason : 'invalid');
      return false;
    }
    this.playing = true;
    this.board.setLocked(true);
    const battleEvents = r.events.flatMap((e) => (e.type === 'battle' ? e.events : []));
    const player = battleEvents.find((e) => e.type === 'player');
    const damage = player?.type === 'player' ? player.total : 0;
    const after = r.state;
    await Promise.all([
      this.board.playResult(
        path,
        after.battle.board,
        boardEventsOf(battleEvents),
        damage,
        this.run.linkContext(),
      ),
      this.battle.play(r.events, before, after),
    ]);
    this.playing = false;
    this.afterStep(after);
    return true;
  }

  private afterStep(s: RunState): void {
    if (s.phase === 'battle') {
      this.board.setLocked(false);
      return;
    }
    this.board.setLocked(true);
    if (s.phase === 'chooseBuff') {
      this.overlay.showBuffs(s, (i) => this.chooseBuff(i));
    } else {
      this.overlay.showEnd(s, () => this.restart());
    }
  }

  chooseBuff(index: number): boolean {
    if (this.run.state.phase !== 'chooseBuff') return false;
    const r = this.run.step({ type: 'chooseBuff', index });
    if (!r.changed) return false;
    this.overlay.hide();
    this.enterFloor(r.state);
    return true;
  }

  restart(): void {
    this.overlay.hide();
    this.run.restart();
    this.board.score = 0;
    this.board.turns = 0;
    this.board.moves.length = 0;
    this.enterFloor(this.run.state);
  }

  private enterFloor(s: RunState): void {
    this.board.setBoard(s.battle.board, this.run.linkContext());
    this.battle.sync(s, true);
    this.board.setLocked(s.phase !== 'battle');
  }

  /** Bot debug: đi một nước (qua đúng đường release của người chơi). */
  async botStep(): Promise<boolean> {
    await this.idle;
    const a = this.run.botMove();
    if (!a) return false;
    const p = this.release(a.path);
    this.idle = p.then(() => undefined);
    return p;
  }

  /**
   * Bot chơi đến khi hết tầng hiện tại (chooseBuff / won / lost) hoặc quá `maxSteps`.
   * Trả phase cuối và số nước đã đi.
   */
  async botPlayFloor(maxSteps = 400): Promise<{ phase: string; steps: number; floor: number }> {
    let steps = 0;
    while (this.run.state.phase === 'battle' && steps < maxSteps) {
      const ok = await this.botStep();
      if (!ok) break;
      steps++;
    }
    await this.idle;
    return { phase: this.run.state.phase, steps, floor: this.run.state.floor };
  }

  /** Debug/test: đặt HP hero của trận hiện tại (kiểm luồng thua → chơi lại). */
  debugSetHeroHp(hp: number): void {
    const s = this.run.state;
    const hs = s.battle.heroState;
    this.run.state = {
      ...s,
      battle: { ...s.battle, heroState: { ...hs, hp: Math.max(1, Math.min(hs.maxHp, hp)) } },
    };
    this.battle.sync(this.run.state);
  }

  debugState(): Record<string, unknown> {
    const s = this.run.state;
    const b = s.battle;
    return {
      ...this.board.debugState(),
      seed: this.setup.seed,
      phase: s.phase,
      floor: s.floor,
      floorCount: s.floorCount,
      wave: b.wave,
      waveCount: b.waves.length,
      round: b.round,
      heroHp: b.heroState.hp,
      heroMaxHp: b.heroState.maxHp,
      shield: b.heroState.shield,
      gauge: b.heroState.gauge,
      cooldowns: [...b.heroState.cooldowns],
      monsters: b.monsters.map((m) => ({
        id: m.id,
        hp: m.hp,
        maxHp: m.maxHp,
        intent: m.intents[m.intentIndex]?.id,
        countdown: m.countdown,
        stun: m.stun,
      })),
      offer: s.offer.map((o) => o.id),
      buffs: s.buffs.map((o) => o.id),
      playing: this.playing,
      overlay: this.overlay.visible ? (this.overlay.el.dataset.kind ?? '') : null,
      skin: this.setup.skin ? this.setup.skin.manifest.skinId : null,
      actions: this.run.log.length,
    };
  }
}
